// FamilyHealthWidget — compact Home-tab version of the SFO Family Health
// dashboard. Renders only for Single Family Office orgs with exactly one
// client (returns null otherwise so non-SFO users see nothing if it ends up
// in their layout).
//
// Surfaces:
//  - hero ring + status badge + family name
//  - 3 weakest-scoring metrics with mini bars
//  - the top recommendation (most impactful next action)
//  - "Open full Family Health" link to the Health tab

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Activity, Shield, FileText, ArrowRight, TrendingUp,
  CheckCircle2, AlertTriangle, Lightbulb, Loader2,
} from 'lucide-react';

const STATUS_CONFIG = {
  excellent:        { label: 'Excellent',       color: 'text-green-700',  bg: 'bg-green-100' },
  good:             { label: 'Good',            color: 'text-amber-700',  bg: 'bg-amber-100' },
  attention_needed: { label: 'Needs Attention', color: 'text-orange-700', bg: 'bg-orange-100' },
  critical:         { label: 'Critical',        color: 'text-red-700',    bg: 'bg-red-100' },
};

const METRIC_META = {
  dataCompleteness: { label: 'Data',       icon: FileText,     weight: 25, route: 'individuals' },
  complianceScore:  { label: 'Compliance', icon: Shield,       weight: 20, route: 'governance' },
  taskHealth:       { label: 'Tasks',      icon: CheckCircle2, weight: 20, route: 'workflow' },
  linkFreshness:    { label: 'Links',      icon: TrendingUp,   weight: 15, route: 'links' },
  activityRecency:  { label: 'Activity',   icon: Activity,     weight: 20, route: 'activity' },
};

function barColor(v) {
  if (v >= 80) return 'bg-green-500';
  if (v >= 60) return 'bg-amber-500';
  if (v >= 40) return 'bg-orange-500';
  return 'bg-red-500';
}

function MiniRing({ score, color, size = 72, strokeWidth = 6 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#EFEAE3" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
                strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
                className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-heading" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

function topRecommendation(b, d) {
  // Pick the lowest-scoring dimension and give a one-liner action.
  const ordered = [
    { key: 'dataCompleteness', v: b.dataCompleteness,
      text: `Capture missing individuals (${d.individuals || 0}) and entities (${d.entities || 0}).` },
    { key: 'complianceScore', v: b.complianceScore,
      text: `Run a compliance review — resolve ${d.overdueTasks || 0} open item${(d.overdueTasks || 0) === 1 ? '' : 's'}.` },
    { key: 'taskHealth', v: b.taskHealth,
      text: `Clear ${d.overdueTasks || 0} overdue task${(d.overdueTasks || 0) === 1 ? '' : 's'} or reassign owners.` },
    { key: 'linkFreshness', v: b.linkFreshness,
      text: `Index source links — only ${d.links || 0} on file.` },
    { key: 'activityRecency', v: b.activityRecency,
      text: 'Log a recent touchpoint (meeting, call, review).' },
  ];
  ordered.sort((a, b) => a.v - b.v);
  const worst = ordered[0];
  if (worst.v >= 80) return { text: 'No critical gaps detected. Maintain the current cadence.', route: null };
  return { text: worst.text, route: METRIC_META[worst.key].route };
}

export default function FamilyHealthWidget({ ctx }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Gate: SFO orgs only. ctx may be undefined when rendered outside the dashboard.
  const isSfo = ctx?.orgType === 'sfo';

  useEffect(() => {
    if (!token || !isSfo) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.getHealthScores(token);
        setData(res.data);
      } catch (e) {
        console.error('FamilyHealthWidget fetch failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isSfo]);

  if (!isSfo) return null;

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-5 flex items-center justify-center" data-testid="family-health-widget-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  const family = data?.scores?.[0];
  if (!family) return null;

  const cfg = STATUS_CONFIG[family.status] || STATUS_CONFIG.critical;
  const b = family.breakdown;
  const d = family.details || {};

  // 3 weakest metrics for the inline summary
  const metricKeys = Object.keys(METRIC_META);
  const ranked = metricKeys
    .map((key) => ({ key, value: b[key] ?? 0 }))
    .sort((a, b) => a.value - b.value)
    .slice(0, 3);

  const rec = topRecommendation(b, d);
  const goTo = (sub) => navigate(`/app/clients/${family.clientId}/${sub}`);

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-gradient-to-br from-white to-[#FAF7F3] p-5" data-testid="family-health-widget">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Hero ring + name */}
        <div className="flex items-center gap-4 sm:w-[300px] shrink-0">
          <MiniRing score={family.score} color={family.color} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading text-base text-[#2C2420] truncate" data-testid="fh-widget-name">{family.clientName}</h3>
              <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', cfg.bg, cfg.color)} data-testid="fh-widget-status">
                {cfg.label}
              </span>
            </div>
            <p className="text-xs text-[#8C8078] mt-0.5">
              {(d.overdueTasks || 0) > 0 ? (
                <span className="text-red-600 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />{d.overdueTasks} overdue task{d.overdueTasks === 1 ? '' : 's'}
                </span>
              ) : (
                <>Family snapshot · {d.individuals || 0} individuals · {d.entities || 0} entities</>
              )}
            </p>
          </div>
        </div>

        {/* 3 weakest metrics */}
        <div className="flex-1 grid grid-cols-3 gap-3" data-testid="fh-widget-metrics">
          {ranked.map(({ key, value }) => {
            const meta = METRIC_META[key];
            const Icon = meta.icon;
            return (
              <button
                key={key}
                onClick={() => goTo(meta.route)}
                className="text-left p-2.5 rounded-lg border border-[#F0EAE3] bg-white/70 hover:border-[#A86A3A]/30 transition-all"
                data-testid={`fh-widget-metric-${key}`}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon className="w-3.5 h-3.5 text-[#8C8078]" />
                  <span className="text-[11px] text-[#5D524D] truncate">{meta.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-[#F5F3F1] rounded-full overflow-hidden">
                    <div className={cn('h-full rounded-full transition-all duration-500', barColor(value))} style={{ width: `${value}%` }} />
                  </div>
                  <span className="text-xs font-medium text-[#2C2420] w-7 text-right">{value}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Top recommendation + CTA */}
      <div className="mt-4 pt-4 border-t border-[#F0EAE3] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Lightbulb className="w-4 h-4 text-[#A86A3A] mt-0.5 shrink-0" />
          <p className="text-sm text-[#2C2420] leading-relaxed" data-testid="fh-widget-recommendation">{rec.text}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rec.route && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => goTo(rec.route)}
              className="h-8 text-xs border-[#E6E0D9] text-[#4A3B32]"
              data-testid="fh-widget-resolve-btn"
            >
              Resolve <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => ctx?.setActiveTab?.('health')}
            className="h-8 text-xs text-[#A86A3A] hover:text-[#8B5730] hover:bg-[#FAF5F0]"
            data-testid="fh-widget-open-full"
          >
            Full view <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
